import type { FC, ReactNode } from 'react'
import type { Profile } from '@/types'

import { useRef } from 'react'
import { ArrowUp } from 'lucide-react'
import { motion, useScroll, useTransform } from 'motion/react'
import { getContactLinks } from '@/lib/contact-links'
import { Container } from '@/components/generals/container'
import { LinkIcon } from '@/components/generals/link-icon'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface FooterProps {
    profile: Profile | null
    year?: number
}

// Dark closing band: the two halves of the name slide together as the page ends.
export const Footer: FC<FooterProps> = ({ profile, year }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const reduced = usePrefersReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end end'] })
    const firstX = useTransform(scrollYProgress, [0, 1], reduced ? ['0%', '0%'] : ['-28%', '0%'])
    const lastX = useTransform(scrollYProgress, [0, 1], reduced ? ['0%', '0%'] : ['28%', '0%'])
    const nameOpacity = useTransform(scrollYProgress, [0, 0.7], reduced ? [1, 1] : [0.2, 1])
    const links = profile ? getContactLinks(profile) : []

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
    }

    return (
        <footer ref={ref} className='relative overflow-hidden bg-foreground text-background'>
            <Container className='pt-16 pb-8 md:pt-20'>
                <div className='flex flex-col gap-10 md:flex-row md:items-end md:justify-between'>
                    <div className='max-w-md'>
                        <p className='font-mono text-xs font-bold uppercase tracking-[0.2em] text-primary'>
                            Thanks for scrolling
                        </p>

                        {profile?.role && (
                            <p className='mt-4 font-heading text-2xl font-bold leading-tight md:text-3xl'>
                                <span data-mdlfy-cms={`profile:${profile.id}:role`}>{profile.role}</span>
                            </p>
                        )}
                    </div>

                    {links.length > 0 && (
                        <ul className='flex flex-wrap gap-3'>
                            {links.map(link => (
                                <li key={link.key}>
                                    <a
                                        href={link.href}
                                        target={link.external ? '_blank' : undefined}
                                        rel={link.external ? 'noopener noreferrer' : undefined}
                                        className='inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-sm transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground'
                                    >
                                        <LinkIcon type={link.key} className='size-4' />

                                        {link.cms ? <span data-mdlfy-cms={link.cms}>{link.label}</span> : link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {profile && (
                    <motion.p
                        style={{ opacity: nameOpacity }}
                        className='mt-16 flex flex-wrap items-baseline justify-between gap-x-[3vw] font-heading text-[clamp(3rem,13vw,13rem)] font-extrabold uppercase leading-[0.85] tracking-[-0.04em] text-primary md:mt-24'
                    >
                        <motion.span style={{ x: firstX }} className='block' data-mdlfy-cms={`profile:${profile.id}:first_name`}>
                            {profile.first_name}
                        </motion.span>

                        <motion.span style={{ x: lastX }} className='block' data-mdlfy-cms={`profile:${profile.id}:last_name`}>
                            {profile.last_name}
                        </motion.span>
                    </motion.p>
                )}

                <div className='mt-12 flex flex-col-reverse gap-4 border-t border-white/15 pt-6 text-sm sm:flex-row sm:items-center sm:justify-between'>
                    <p className='text-white/70'>
                        <span>©</span>
                        {year && <span> {year}</span>}

                        {profile && (
                            <>
                                {' '}
                                <span data-mdlfy-cms={`profile:${profile.id}:first_name`}>{profile.first_name}</span>
                                {' '}
                                <span data-mdlfy-cms={`profile:${profile.id}:last_name`}>{profile.last_name}</span>
                            </>
                        )}
                    </p>

                    <button
                        type='button'
                        onClick={scrollToTop}
                        className='inline-flex items-center gap-2 self-start font-mono text-xs font-bold uppercase tracking-[0.16em] transition-colors hover:text-primary sm:self-auto'
                    >
                        Back to top
                        <ArrowUp aria-hidden='true' className='size-4' />
                    </button>
                </div>
            </Container>
        </footer>
    )
}
